import React, { useEffect, useMemo, useState } from 'react';
import DashboardModal from './DashboardModal';
import QuotesCategoryColumnView, {
    normalizeDealHealthCategory,
    type QuotesCategoryColumnEntry,
    type QuotesCategoryQuote,
} from './QuotesCategoryColumnView';
import './oneButton-css/DealHealthQuotesModal.css';

export type DealHealthQuote = QuotesCategoryQuote;
export { normalizeDealHealthCategory };

export interface DealHealthQuotesModalProps {
    open: boolean;
    onClose: () => void;
    quotes: DealHealthQuote[];
    theme?: 'dark' | 'light';
}

export const DealHealthQuotesModal: React.FC<DealHealthQuotesModalProps> = ({
    open,
    onClose,
    quotes,
    theme = 'light',
}) => {
    const [selectedQuoteIds, setSelectedQuoteIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) setSelectedQuoteIds(new Set());
    }, [open]);

    const entries = useMemo((): QuotesCategoryColumnEntry[] => {
        return quotes.map((quote) => ({
            quote,
            selected: selectedQuoteIds.has(quote.id),
            onToggleSelect: (checked) => {
                setSelectedQuoteIds((prev) => {
                    const next = new Set(prev);
                    if (checked) next.add(quote.id);
                    else next.delete(quote.id);
                    return next;
                });
            },
        }));
    }, [quotes, selectedQuoteIds]);

    const categories = useMemo(() => {
        const set = new Set(quotes.map((q) => normalizeDealHealthCategory(q.product)));
        return set.size;
    }, [quotes]);

    const selectionSummary = useMemo(() => {
        if (selectedQuoteIds.size === 0) return null;
        const byCategory = new Map<string, string>();
        quotes.forEach((quote) => {
            if (!selectedQuoteIds.has(quote.id)) return;
            const category = normalizeDealHealthCategory(quote.product);
            if (!byCategory.has(category)) byCategory.set(category, quote.id);
        });
        return Array.from(byCategory.entries())
            .map(([category, id]) => `${category}: #${id}`)
            .join(' · ');
    }, [quotes, selectedQuoteIds]);

    return (
        <DashboardModal
            title="Deal Health"
            open={open}
            onClose={onClose}
            width="min(calc(100vw - 48px), 1440px)"
            maskClosable
            theme={theme}
            zIndex={10100}
            rootClassName="obd-deal-health-quotes-modal-root"
            className="obd-deal-health-quotes-modal"
        >
            <div className="obd-deal-health-quotes-modal__body">
                <div className="obd-deal-health-quotes-modal__summary">
                    <p className="obd-deal-health-quotes-modal__intro">
                        Pick quotes by product category to review deal health. Each column is one category — compare
                        partner options side by side.
                    </p>
                    <div className="obd-deal-health-quotes-modal__stats">
                        <span className="obd-deal-health-quotes-modal__stat">
                            {quotes.length} quote{quotes.length === 1 ? '' : 's'}
                        </span>
                        <span className="obd-deal-health-quotes-modal__stat">
                            {categories} categor{categories === 1 ? 'y' : 'ies'}
                        </span>
                        <span className="obd-deal-health-quotes-modal__stat obd-deal-health-quotes-modal__stat--selected">
                            {selectedQuoteIds.size} selected
                        </span>
                    </div>
                    {selectionSummary ? (
                        <p className="obd-deal-health-quotes-modal__selection" aria-live="polite">
                            {selectionSummary}
                        </p>
                    ) : null}
                </div>

                <QuotesCategoryColumnView entries={entries} />
            </div>
        </DashboardModal>
    );
};

export default DealHealthQuotesModal;
